import { HttpService, ObservableCollection, QueryBus, Repository, RepositoryBase, TaggedUrl, Tags, Weaver } from '@skylab-shared/core';
import { AddressModel, ClientBankAccountModel, ClientDTO, ClientModel } from '@skylab-shared/dataModel';
import { pickBy } from '@skylab-shared/utils';
import { serializeAddressModel } from '../common/serialziation-utils';
import { ClientBO } from './client.bo';

@Repository({
    boType: ClientBO,
    apiController: TaggedUrl.join('clients', [Tags.Api]),
    dto: {
        get: ClientDTO,
        update: ClientDTO
    }
})
export class ClientRepositoryBase extends RepositoryBase<ClientBO, ClientModel, ClientDTO, ClientDTO> {

    public constructor(
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus,
        http: HttpService
    ) {
        super(http);
    }

    protected createBO(): ClientBO {
        return new ClientBO(this.weaver, this.queryBus);
    }

    //#region Mapping
    protected async preparePlainObjectAsync(model: ClientModel): Promise<ClientDTO> {
        return {
            id: model.id,
            version: model.version,
            name: model.name,
            kind: model.kind,
            tin: model.tin,
            tinKind: model.tinKind,

            address: model.address,
            city: model.city,
            emails: model.emails,
            phone1: model.phone1,
            phone2: model.phone2,
            skype: model.skype,
            website: model.website,
            zipCode: model.zipCode,

            deliveryAddress: model.deliveryAddress && model.deliveryAddress.isFilled() ? serializeAddressModel(model.deliveryAddress) : null,

            bankAccounts: model.bankAccounts.map(item => ({
                default: item.default,
                bankName: item.bankName,
                number: item.number,
                whiteListStatus: item.status,
                whiteListLastDateCheck: item.statusDate,
                whiteListCheckingId: item.statusId
            })),
            note: model.note,

            buyerId: model.buyerId,
            buyerRole: model.buyerId ? model.buyerRole : null,
            internalIdentificationNumber: model.buyerId ? model.internalIdentificationNumber : null,
            countryId: model.countryId,
            groupId: model.clientGroupId,

            associatedClient: model.associatedClient,
            defaultDiscount: model.defaultDiscount,
            defaultDueDate: model.defaultDueDate,
            defaultPaymentMethodId: model.defaultPaymentMethodId,
            favourite: model.favourite
        };
    }

    protected async fromPlainObjectAsync(item: ClientDTO): Promise<ClientModel> {
        return new ClientModel(pickBy<ClientModel>({
            id: item.id,
            version: item.version,

            name: item.name,
            kind: item.kind,
            tin: item.tin,
            tinKind: item.tinKind,

            address: item.address,
            city: item.city,
            emails: item.emails,
            phone1: item.phone1,
            phone2: item.phone2,
            skype: item.skype,
            website: item.website,
            zipCode: item.zipCode,

            deliveryAddress: item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
                name: item.deliveryAddress.name,
                zipCode: item.deliveryAddress.zipCode,
                line1: item.deliveryAddress.address,
                city: item.deliveryAddress.city,
                countryId: item.deliveryAddress.countryId
            }, val => val !== void 0)) : null,

            bankAccounts: new ObservableCollection(item.bankAccounts.map(bankAccount => (new ClientBankAccountModel(pickBy<ClientBankAccountModel>({
                default: bankAccount.default,
                bankName: bankAccount.bankName,
                number: bankAccount.number,
                status: bankAccount.whiteListStatus,
                statusDate: bankAccount.whiteListLastDateCheck,
                statusId: bankAccount.whiteListCheckingId
            }, val => val !== void 0))))),

            note: item.note,

            buyerId: item.buyerId,
            buyerRole: item.buyerRole,
            internalIdentificationNumber: item.internalIdentificationNumber,
            clientGroupId: item.groupId,
            countryId: item.countryId,

            associatedClient: item.associatedClient,
            defaultDiscount: item.defaultDiscount,
            defaultPaymentMethodId: item.defaultPaymentMethodId,
            defaultDueDate: item.defaultDueDate,
            favourite: item.favourite
        }, val => val !== void 0));
    }

    //#endregion
}
